/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import ExcelJS from 'exceljs';
import { Folio, Client, CostCenter, PoRecord } from '../types.ts';
import { CORPORATE_LOGO_BASE64 } from './corporateLogoBase64.ts';

export interface FolioExcelExportOptions {
  fileName?: string;
  clientName?: string;
  poNumber?: string;
  annualBudgetMXN?: number;
  previousBalanceMXN?: number;
  availableBalanceMXN?: number;
  filterDescription?: string;
  generatedBy?: string;
  poRecords?: PoRecord[];
}

/**
 * Generates an executive, professionally styled Excel file for Folio History & Traceability.
 * Includes embedded corporate logo, financial KPI metrics, structured tables,
 * precise typography, borders, currency formatting, and multiple breakdown sheets.
 */
export async function exportFolioHistoryToExcel(
  folios: Folio[],
  options: FolioExcelExportOptions = {}
): Promise<void> {
  const {
    clientName = 'Socio Comercial',
    poNumber = 'VIGENTE',
    annualBudgetMXN = 0,
    previousBalanceMXN = 0,
    availableBalanceMXN = 0,
    filterDescription = 'Historial Completo de Folios y Trazabilidad',
    generatedBy = 'Sistema Integral de Control (SIC PRO) • Grupo ISN / SNF',
    poRecords = []
  } = options;

  const cleanClientFileName = clientName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const dateStamp = new Date().toISOString().split('T')[0];
  const outputFileName = options.fileName || `Historial_Folios_${cleanClientFileName}_${dateStamp}.xlsx`;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SIC PRO SNF - Grupo ISN';
  workbook.lastModifiedBy = generatedBy;
  workbook.created = new Date();
  workbook.modified = new Date();

  // Helper to add corporate logo
  const addLogoToSheet = (sheet: ExcelJS.Worksheet) => {
    try {
      if (CORPORATE_LOGO_BASE64 && CORPORATE_LOGO_BASE64.startsWith('data:image')) {
        const logoId = workbook.addImage({
          base64: CORPORATE_LOGO_BASE64,
          extension: 'jpeg'
        });
        sheet.addImage(logoId, {
          tl: { col: 0.15, row: 0.2 },
          ext: { width: 140, height: 44 }
        });
      }
    } catch (e) {
      console.warn('Could not render logo in worksheet:', e);
    }
  };

  // Border styles
  const thinBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
  };

  const headerBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'medium', color: { argb: 'FF0F172A' } },
    left: { style: 'thin', color: { argb: 'FF334155' } },
    bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
    right: { style: 'thin', color: { argb: 'FF334155' } }
  };

  // Calculation Metrics
  const totalPieces = folios.reduce((acc, f) => acc + f.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0), 0);
  const totalAmountMXN = folios.reduce((acc, f) => acc + f.items.reduce((s, it) => s + (Number(it.totalMXN) || 0), 0), 0);
  const grConfirmedCount = folios.filter(f => f.grStatus === 'CONFIRMADO' || (f.grStatus as string) === 'GR FACTURADO' || (f.grStatus as string) === 'GR CONFIRMADO').length;
  const grPendingCount = folios.length - grConfirmedCount;

  // ==========================================
  // SHEET 1: HISTORIAL GENERAL DE FOLIOS
  // ==========================================
  const wsMain = workbook.addWorksheet('Historial de Folios', {
    views: [{ showGridLines: true }],
    pageSetup: {
      orientation: 'landscape',
      paperSize: 9, // A4
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 }
    }
  });

  addLogoToSheet(wsMain);

  // Set Row Heights for Header
  wsMain.getRow(1).height = 18;
  wsMain.getRow(2).height = 20;
  wsMain.getRow(3).height = 18;
  wsMain.getRow(4).height = 16;
  wsMain.getRow(5).height = 12;

  // Header Title Text
  wsMain.mergeCells('D1:N1');
  const titleCell = wsMain.getCell('D1');
  titleCell.value = 'GRUPO ISN • SERVICIOS NACIONALES Y FORÁNEOS (SNF)';
  titleCell.font = { name: 'Calibri', size: 13, bold: true, color: { argb: 'FF991B1B' } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

  wsMain.mergeCells('D2:N2');
  const subTitleCell = wsMain.getCell('D2');
  subTitleCell.value = 'REPORTE EJECUTIVO DE HISTORIAL DE FOLIOS, TRAZABILIDAD Y ÓRDENES DE COMPRA';
  subTitleCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF0F172A' } };
  subTitleCell.alignment = { horizontal: 'left', vertical: 'middle' };

  wsMain.mergeCells('D3:N3');
  const metaCell = wsMain.getCell('D3');
  metaCell.value = `CLIENTE: ${clientName.toUpperCase()}  |  FECHA REPORTE: ${new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}  |  ORDEN DE COMPRA ACTIVA: ${poNumber}`;
  metaCell.font = { name: 'Calibri', size: 9.5, italic: true, color: { argb: 'FF475569' } };
  metaCell.alignment = { horizontal: 'left', vertical: 'middle' };

  wsMain.mergeCells('D4:N4');
  const filterCell = wsMain.getCell('D4');
  filterCell.value = `FILTRO APLICADO: ${filterDescription}  |  GENERADO POR: ${generatedBy}`;
  filterCell.font = { name: 'Calibri', size: 8.5, color: { argb: 'FF64748B' } };
  filterCell.alignment = { horizontal: 'left', vertical: 'middle' };

  // KPI Financial Cards (Rows 6-7)
  wsMain.getRow(6).height = 20;
  wsMain.getRow(7).height = 24;

  const kpis = [
    { rangeTitle: 'A6:C6', rangeVal: 'A7:C7', title: 'ORDEN DE COMPRA VIGENTE', val: poNumber, format: '@', bg: 'FFF1F5F9', valColor: 'FF0F172A', isBold: true },
    { rangeTitle: 'D6:E6', rangeVal: 'D7:E7', title: 'PRESUPUESTO NETO PO', val: annualBudgetMXN + previousBalanceMXN, format: '"$"#,##0.00', bg: 'FFF8FAFC', valColor: 'FF0284C7', isBold: true },
    { rangeTitle: 'F6:G6', rangeVal: 'F7:G7', title: 'TOTAL CONSUMIDO ($ MXN)', val: totalAmountMXN, format: '"$"#,##0.00', bg: 'FFF8FAFC', valColor: 'FFDC2626', isBold: true },
    { rangeTitle: 'H6:I6', rangeVal: 'H7:I7', title: 'SALDO DISPONIBLE', val: availableBalanceMXN, format: '"$"#,##0.00', bg: availableBalanceMXN >= 0 ? 'FFF0FDF4' : 'FFFEF2F2', valColor: availableBalanceMXN >= 0 ? 'FF16A34A' : 'FFDC2626', isBold: true },
    { rangeTitle: 'J6:K6', rangeVal: 'J7:K7', title: 'FOLIOS Y PIEZAS EN REPORTE', val: `${folios.length} Folios / ${totalPieces.toLocaleString()} Pzas`, format: '@', bg: 'FFF8FAFC', valColor: 'FF0F172A', isBold: true },
    { rangeTitle: 'L6:N6', rangeVal: 'L7:N7', title: 'RECEPCIÓN PLANTA (GR)', val: `${grConfirmedCount} Confirmados / ${grPendingCount} Pendientes`, format: '@', bg: 'FFF8FAFC', valColor: 'FF059669', isBold: true }
  ];

  kpis.forEach(k => {
    wsMain.mergeCells(k.rangeTitle);
    wsMain.mergeCells(k.rangeVal);

    const tCell = wsMain.getCell(k.rangeTitle.split(':')[0]);
    tCell.value = k.title;
    tCell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: 'FF475569' } };
    tCell.alignment = { horizontal: 'center', vertical: 'middle' };
    tCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
    tCell.border = thinBorder;

    const vCell = wsMain.getCell(k.rangeVal.split(':')[0]);
    vCell.value = k.val;
    vCell.font = { name: 'Calibri', size: 11, bold: k.isBold, color: { argb: k.valColor } };
    vCell.alignment = { horizontal: 'center', vertical: 'middle' };
    vCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: k.bg } };
    vCell.border = thinBorder;
    if (k.format !== '@') {
      vCell.numFmt = k.format;
    }
  });

  // Table Column Headers (Row 9)
  const startRow = 9;
  wsMain.getRow(startRow).height = 28;

  const headers = [
    { header: 'N° FOLIO', key: 'id', width: 16, align: 'center' },
    { header: 'FECHA CREACIÓN', key: 'createdDate', width: 17, align: 'center' },
    { header: 'FECHA EMBARQUE', key: 'shipmentDate', width: 16, align: 'center' },
    { header: 'PLANTA / SEDE', key: 'costCenterName', width: 22, align: 'left' },
    { header: 'DESTINO', key: 'destination', width: 24, align: 'left' },
    { header: 'CHOFER AUTORIZADO', key: 'driverName', width: 20, align: 'left' },
    { header: 'N° ECON.', key: 'economicNumber', width: 13, align: 'center' },
    { header: 'ESTATUS PROCESO', key: 'status', width: 20, align: 'center' },
    { header: 'ORDEN DE COMPRA', key: 'poNumber', width: 18, align: 'center' },
    { header: 'ESTATUS GR', key: 'grStatus', width: 16, align: 'center' },
    { header: 'TOTAL PZAS', key: 'totalPieces', width: 14, align: 'right' },
    { header: 'MONTO TOTAL MXN', key: 'totalMXN', width: 18, align: 'right' },
    { header: 'MATERIALES Y PIEZAS DESGLOSADAS', key: 'itemsSummary', width: 38, align: 'left' },
    { header: 'NOTAS / OBSERVACIONES', key: 'notes', width: 26, align: 'left' }
  ];

  headers.forEach((h, idx) => {
    const colNumber = idx + 1;
    const cell = wsMain.getCell(startRow, colNumber);
    cell.value = h.header;
    cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = headerBorder;
    wsMain.getColumn(colNumber).width = h.width;
  });

  // Populate Data Rows
  let currentRow = startRow + 1;

  folios.forEach((folio, fIndex) => {
    const row = wsMain.getRow(currentRow);
    row.height = 24;

    const fPieces = folio.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
    const fTotal = folio.items.reduce((s, it) => s + (Number(it.totalMXN) || 0), 0);
    const itemsDesc = folio.items.map(it => `${it.quantity} pzas • ${it.description}`).join(' | ');

    const isEven = fIndex % 2 === 0;
    const rowBg = isEven ? 'FFFFFFFF' : 'FFF8FAFC';

    // 1. Folio ID
    const c1 = row.getCell(1);
    c1.value = folio.id;
    c1.font = { name: 'Consolas', size: 9.5, bold: true, color: { argb: 'FF991B1B' } };
    c1.alignment = { horizontal: 'center', vertical: 'middle' };

    // 2. Created Date
    const c2 = row.getCell(2);
    c2.value = folio.createdDate ? folio.createdDate.substring(0, 16).replace('T', ' ') : 'N/A';
    c2.alignment = { horizontal: 'center', vertical: 'middle' };
    c2.font = { name: 'Calibri', size: 9, color: { argb: 'FF334155' } };

    // 3. Shipment Date
    const c3 = row.getCell(3);
    c3.value = folio.shipmentDate ? folio.shipmentDate.substring(0, 10) : 'Pendiente';
    c3.alignment = { horizontal: 'center', vertical: 'middle' };
    c3.font = { name: 'Calibri', size: 9, color: { argb: 'FF334155' } };

    // 4. Cost Center / Planta
    const c4 = row.getCell(4);
    c4.value = folio.costCenterName || 'Sede Principal';
    c4.alignment = { horizontal: 'left', vertical: 'middle' };
    c4.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };

    // 5. Destination
    const c5 = row.getCell(5);
    c5.value = folio.destination || 'Planta Destino';
    c5.alignment = { horizontal: 'left', vertical: 'middle' };
    c5.font = { name: 'Calibri', size: 9, color: { argb: 'FF334155' } };

    // 6. Driver
    const c6 = row.getCell(6);
    c6.value = folio.driverName || 'No Asignado';
    c6.alignment = { horizontal: 'left', vertical: 'middle' };
    c6.font = { name: 'Calibri', size: 9, color: { argb: 'FF1E293B' } };

    // 7. Economic Number
    const c7 = row.getCell(7);
    c7.value = folio.economicNumber || 'N/A';
    c7.alignment = { horizontal: 'center', vertical: 'middle' };
    c7.font = { name: 'Consolas', size: 9, color: { argb: 'FF475569' } };

    // 8. Process Stage
    const c8 = row.getCell(8);
    c8.value = (folio.status || 'RECOLECCION').replace(/_/g, ' ');
    c8.alignment = { horizontal: 'center', vertical: 'middle' };
    c8.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0369A1' } };

    // 9. PO Number (Si está integrado muestra la PO limpia sin prefijo PO-, si no "NO INTEGRADA PO" en negritas)
    const rawPoVal = (folio.poNumber || '').trim();
    const cleanPoVal = rawPoVal.replace(/^PO\s*[-_:]\s*/i, '').replace(/^PO\s+/i, '').trim();
    const isPoAssigned = Boolean(
      cleanPoVal &&
      cleanPoVal.toUpperCase() !== 'SIN PO' &&
      cleanPoVal.toUpperCase() !== 'S/PO' &&
      cleanPoVal.toUpperCase() !== 'NO INTEGRADA PO' &&
      cleanPoVal.toUpperCase() !== 'NO INTEGRADA' &&
      cleanPoVal.toUpperCase() !== 'SIN_PO'
    );
    const c9 = row.getCell(9);
    c9.value = isPoAssigned ? cleanPoVal : 'NO INTEGRADA PO';
    c9.alignment = { horizontal: 'center', vertical: 'middle' };
    c9.font = { name: 'Consolas', size: 9, bold: true, color: { argb: isPoAssigned ? 'FF334155' : 'FFDC2626' } };

    // 10. GR Status
    const isGrConfirmed = folio.grStatus === 'CONFIRMADO' || (folio.grStatus as string) === 'GR FACTURADO' || (folio.grStatus as string) === 'GR CONFIRMADO';
    const c10 = row.getCell(10);
    c10.value = isGrConfirmed ? 'CONFIRMADO' : 'POR FACTURAR';
    c10.alignment = { horizontal: 'center', vertical: 'middle' };
    c10.font = { name: 'Calibri', size: 9, bold: true, color: { argb: isGrConfirmed ? 'FF15803D' : 'FFB45309' } };

    // 11. Pieces
    const c11 = row.getCell(11);
    c11.value = fPieces;
    c11.numFmt = '#,##0';
    c11.alignment = { horizontal: 'right', vertical: 'middle' };
    c11.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FF0F172A' } };

    // 12. Amount MXN
    const c12 = row.getCell(12);
    c12.value = fTotal;
    c12.numFmt = '"$"#,##0.00';
    c12.alignment = { horizontal: 'right', vertical: 'middle' };
    c12.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FF991B1B' } };

    // 13. Items breakdown
    const c13 = row.getCell(13);
    c13.value = itemsDesc || 'Sin desglose';
    c13.alignment = { horizontal: 'left', vertical: 'middle' };
    c13.font = { name: 'Calibri', size: 8.5, color: { argb: 'FF475569' } };

    // 14. Notes
    const c14 = row.getCell(14);
    c14.value = folio.notes || '-';
    c14.alignment = { horizontal: 'left', vertical: 'middle' };
    c14.font = { name: 'Calibri', size: 8.5, color: { argb: 'FF64748B' } };

    // Apply backgrounds and borders to all 14 cells
    for (let c = 1; c <= 14; c++) {
      const cell = row.getCell(c);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
      cell.border = thinBorder;
    }

    currentRow++;
  });

  // Total Summary Footer Row
  const totalRow = wsMain.getRow(currentRow);
  totalRow.height = 26;

  wsMain.mergeCells(`A${currentRow}:J${currentRow}`);
  const labelTotalCell = wsMain.getCell(`A${currentRow}`);
  labelTotalCell.value = `TOTALES ACUMULADOS (${folios.length} FOLIOS EVALUADOS):`;
  labelTotalCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF0F172A' } };
  labelTotalCell.alignment = { horizontal: 'right', vertical: 'middle' };
  labelTotalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  // Total Pieces Formula
  const sumPiecesCell = totalRow.getCell(11);
  if (folios.length > 0) {
    sumPiecesCell.value = { formula: `SUM(K${startRow + 1}:K${currentRow - 1})` };
  } else {
    sumPiecesCell.value = 0;
  }
  sumPiecesCell.numFmt = '#,##0';
  sumPiecesCell.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FF0F172A' } };
  sumPiecesCell.alignment = { horizontal: 'right', vertical: 'middle' };
  sumPiecesCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  // Total Amount Formula
  const sumAmountCell = totalRow.getCell(12);
  if (folios.length > 0) {
    sumAmountCell.value = { formula: `SUM(L${startRow + 1}:L${currentRow - 1})` };
  } else {
    sumAmountCell.value = 0;
  }
  sumAmountCell.numFmt = '"$"#,##0.00';
  sumAmountCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF991B1B' } };
  sumAmountCell.alignment = { horizontal: 'right', vertical: 'middle' };
  sumAmountCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const emptySummary1 = totalRow.getCell(13);
  emptySummary1.value = '';
  emptySummary1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const emptySummary2 = totalRow.getCell(14);
  emptySummary2.value = '';
  emptySummary2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const summaryBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FF0F172A' } },
    bottom: { style: 'double', color: { argb: 'FF0F172A' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
  };

  for (let c = 1; c <= 14; c++) {
    totalRow.getCell(c).border = summaryBorder;
  }

  // ====================================================
  // SHEET 2: DESGLOSE DETALLADO DE ÍTEMS Y MATERIALES
  // ====================================================
  const wsItems = workbook.addWorksheet('Desglose de Ítems', {
    views: [{ showGridLines: true }],
    pageSetup: {
      orientation: 'landscape',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0
    }
  });

  addLogoToSheet(wsItems);

  wsItems.mergeCells('D1:J1');
  const t2 = wsItems.getCell('D1');
  t2.value = 'DESGLOSE DE MATERIALES, PARTES Y PIEZAS POR FOLIO';
  t2.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF991B1B' } };

  wsItems.mergeCells('D2:J2');
  const st2 = wsItems.getCell('D2');
  st2.value = `CLIENTE: ${clientName.toUpperCase()}  |  TOTAL REGISTROS DE LÍNEA`;
  st2.font = { name: 'Calibri', size: 9.5, italic: true, color: { argb: 'FF475569' } };

  const itemHeaders = [
    { header: 'N° FOLIO', width: 16 },
    { header: 'FECHA FOLIO', width: 16 },
    { header: 'PLANTA / SEDE', width: 22 },
    { header: 'ID / CÓDIGO ÍTEM', width: 18 },
    { header: 'DESCRIPCIÓN DE LA PIEZA O SERVICIO', width: 36 },
    { header: 'CANTIDAD (PZAS)', width: 16 },
    { header: 'PRECIO UNITARIO ($)', width: 18 },
    { header: 'IMPORTE TOTAL ($ MXN)', width: 20 },
    { header: 'ESTATUS FOLIO', width: 18 },
    { header: 'ORDEN DE COMPRA', width: 18 }
  ];

  const itemStartRow = 5;
  wsItems.getRow(itemStartRow).height = 24;

  itemHeaders.forEach((ih, iIdx) => {
    const colNum = iIdx + 1;
    const cell = wsItems.getCell(itemStartRow, colNum);
    cell.value = ih.header;
    cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = headerBorder;
    wsItems.getColumn(colNum).width = ih.width;
  });

  let curItemRow = itemStartRow + 1;

  folios.forEach((f) => {
    f.items.forEach((it, itIdx) => {
      const iRow = wsItems.getRow(curItemRow);
      iRow.height = 20;
      const isEv = curItemRow % 2 === 0;
      const rBg = isEv ? 'FFFFFFFF' : 'FFF8FAFC';

      // 1. Folio ID
      const ic1 = iRow.getCell(1);
      ic1.value = f.id;
      ic1.font = { name: 'Consolas', size: 9, bold: true, color: { argb: 'FF991B1B' } };
      ic1.alignment = { horizontal: 'center', vertical: 'middle' };

      // 2. Date
      const ic2 = iRow.getCell(2);
      ic2.value = (f.shipmentDate || f.createdDate || '').substring(0, 10);
      ic2.alignment = { horizontal: 'center', vertical: 'middle' };
      ic2.font = { name: 'Calibri', size: 9 };

      // 3. Planta
      const ic3 = iRow.getCell(3);
      ic3.value = f.costCenterName || 'Sede Principal';
      ic3.font = { name: 'Calibri', size: 9 };

      // 4. Goods Item Code
      const ic4 = iRow.getCell(4);
      ic4.value = it.goodsItemId || 'N/A';
      ic4.alignment = { horizontal: 'center', vertical: 'middle' };
      ic4.font = { name: 'Consolas', size: 9 };

      // 5. Description
      const ic5 = iRow.getCell(5);
      ic5.value = it.description;
      ic5.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF0F172A' } };

      // 6. Quantity
      const ic6 = iRow.getCell(6);
      ic6.value = Number(it.quantity) || 0;
      ic6.numFmt = '#,##0';
      ic6.alignment = { horizontal: 'right', vertical: 'middle' };
      ic6.font = { name: 'Calibri', size: 9, bold: true };

      // 7. Unit Price
      const ic7 = iRow.getCell(7);
      ic7.value = Number(it.unitPriceMXN) || 0;
      ic7.numFmt = '"$"#,##0.00';
      ic7.alignment = { horizontal: 'right', vertical: 'middle' };
      ic7.font = { name: 'Calibri', size: 9 };

      // 8. Total MXN
      const ic8 = iRow.getCell(8);
      ic8.value = Number(it.totalMXN) || 0;
      ic8.numFmt = '"$"#,##0.00';
      ic8.alignment = { horizontal: 'right', vertical: 'middle' };
      ic8.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF991B1B' } };

      // 9. Folio Status
      const ic9 = iRow.getCell(9);
      ic9.value = (f.status || '').replace(/_/g, ' ');
      ic9.alignment = { horizontal: 'center', vertical: 'middle' };
      ic9.font = { name: 'Calibri', size: 8.5 };

      // 10. PO
      const ic10 = iRow.getCell(10);
      ic10.value = f.poNumber || poNumber || 'SIN PO';
      ic10.alignment = { horizontal: 'center', vertical: 'middle' };
      ic10.font = { name: 'Consolas', size: 8.5 };

      for (let c = 1; c <= 10; c++) {
        const cl = iRow.getCell(c);
        cl.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rBg } };
        cl.border = thinBorder;
      }

      curItemRow++;
    });
  });

  // Footer for Sheet 2
  const iTotalRow = wsItems.getRow(curItemRow);
  iTotalRow.height = 24;
  wsItems.mergeCells(`A${curItemRow}:E${curItemRow}`);
  const iTotalLabel = wsItems.getCell(`A${curItemRow}`);
  iTotalLabel.value = 'TOTAL MATERIALES Y PIEZAS PROCESADAS:';
  iTotalLabel.font = { name: 'Calibri', size: 9.5, bold: true };
  iTotalLabel.alignment = { horizontal: 'right', vertical: 'middle' };
  iTotalLabel.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const iTotPcs = iTotalRow.getCell(6);
  if (curItemRow > itemStartRow + 1) {
    iTotPcs.value = { formula: `SUM(F${itemStartRow + 1}:F${curItemRow - 1})` };
  } else {
    iTotPcs.value = 0;
  }
  iTotPcs.numFmt = '#,##0';
  iTotPcs.font = { name: 'Calibri', size: 10, bold: true };
  iTotPcs.alignment = { horizontal: 'right', vertical: 'middle' };
  iTotPcs.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const iTotBlank = iTotalRow.getCell(7);
  iTotBlank.value = '';
  iTotBlank.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const iTotMxn = iTotalRow.getCell(8);
  if (curItemRow > itemStartRow + 1) {
    iTotMxn.value = { formula: `SUM(H${itemStartRow + 1}:H${curItemRow - 1})` };
  } else {
    iTotMxn.value = 0;
  }
  iTotMxn.numFmt = '"$"#,##0.00';
  iTotMxn.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FF991B1B' } };
  iTotMxn.alignment = { horizontal: 'right', vertical: 'middle' };
  iTotMxn.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const iTotBlank2 = iTotalRow.getCell(9);
  iTotBlank2.value = '';
  iTotBlank2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  const iTotBlank3 = iTotalRow.getCell(10);
  iTotBlank3.value = '';
  iTotBlank3.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

  for (let c = 1; c <= 10; c++) {
    iTotalRow.getCell(c).border = summaryBorder;
  }

  // ====================================================
  // GENERATE AND TRIGGER BROWSER DOWNLOAD
  // ====================================================
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = outputFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
